<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            <h3>{{ translate("admin_payment.payment_settings") }}</h3>
        </template>
        <template v-slot:body>
            <div class="columns" v-if="initDone">
                <div class="column">
                    <div class="field">
                        <Checkbox2 v-model="enablePaymentConfirmationUploads">
                            {{ translate("admin_payments.sending_pdfs_feature") }}
                        </Checkbox2>
                    </div>

                    <div class="field">
                        <Checkbox2 v-model="enablePaymentsAfterDueDate">
                            {{ translate("event_setting.enablePaymentsAfterDueDate") }}
                        </Checkbox2>
                    </div>

                    <div class="field">
                        <Checkbox2 v-model="automaticallyMakeNewRegistrationCandidate">
                            {{ translate("event_setting.automaticallyMakeNewRegistrationCandidate") }}
                        </Checkbox2>
                    </div>
                </div>
            </div>
            <div class="columns is-centered is-mobile" v-else>
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="updateInProgress" class="button is-primary" @click="saveSettings">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import Checkbox2 from "../../Common/Checkbox2.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import { translate } from "../../Common/translations";
    import eventSettingsApi from "../../Api/eventSettingsApi";
    import { successToast } from "../../Common/toasts";
    import { handleError } from "../../Common/errorHandler";

    const props = defineProps<{
        eventId: number;
    }>();

    const modal = ref();
    const initDone = ref(false);
    const updateInProgress = ref(false);
    const enablePaymentConfirmationUploads = ref(false);
    const enablePaymentsAfterDueDate = ref(false);
    const automaticallyMakeNewRegistrationCandidate = ref(false);

    function open() {
        initDone.value = false;
        modal.value.open();

        fetchConfirmationUploadsFeature().finally(() => {
            initDone.value = true;
        });
    }

    defineExpose({ open });

    function close() {
        modal.value.close();
    }

    function saveSettings() {
        updateInProgress.value = true;

        Promise.all([
            eventSettingsApi.updateSetting(props.eventId, "enablePaymentConfirmationUploads", enablePaymentConfirmationUploads.value),
            eventSettingsApi.updateSetting(props.eventId, "enablePaymentsAfterDueDate", enablePaymentsAfterDueDate.value),
            eventSettingsApi.updateSetting(
                props.eventId,
                "automaticallyMakeNewRegistrationCandidate",
                automaticallyMakeNewRegistrationCandidate.value,
            ),
        ])
            .then(() => {
                successToast(translate("common.changes_have_been_saved"));
                close();
            })
            .catch(handleError)
            .finally(() => {
                updateInProgress.value = false;
            });
    }

    function fetchConfirmationUploadsFeature() {
        return Promise.all([
            eventSettingsApi.getSingleSetting(props.eventId, "enablePaymentConfirmationUploads"),
            eventSettingsApi.getSingleSetting(props.eventId, "enablePaymentsAfterDueDate"),
            eventSettingsApi.getSingleSetting(props.eventId, "automaticallyMakeNewRegistrationCandidate"),
        ])
            .then(([confirmationUploads, paymentsAfterDueDate, automaticCandidates]) => {
                enablePaymentConfirmationUploads.value = confirmationUploads.value;
                enablePaymentsAfterDueDate.value = paymentsAfterDueDate.value;
                automaticallyMakeNewRegistrationCandidate.value = automaticCandidates.value;
            })
            .catch(handleError);
    }
</script>

<style scoped></style>
